import { createPlayer, type Player } from '../managed';
import { PlaybackError } from '../core/errors';
import { type WaitForLive } from '../core/waitForLive';
import { type FrameToHost, type HostToFrame, isOddinMessage, PROTOCOL_VERSION } from './protocol';

export interface EmbedConfig {
  baseUrl: string;
  matchUrn: string;
  /** DEV/DEMO ONLY when read from the query string — in production inject server-side. */
  apiKey: string;
  /** Origin to post events to and accept commands from. Required for the bridge. */
  parentOrigin?: string;
  autoplay?: boolean;
  muted?: boolean;
  waitForLive?: WaitForLive;
  /**
   * QoE beacons, forwarded verbatim to {@link createPlayer}. Same meaning as
   * there: `false` opts out, `{ endpoint }` names a beacons host.
   *
   * Mode C needs this for the same reason Mode A does, and until now had no
   * way to say it. An embed whose `baseUrl` is not a `feed[-dev].<domain>`
   * host — a CDN or gateway fronting the streams API — derives no beacons
   * endpoint, so analytics is off and stays off. Without this option the
   * only remedy was to move `baseUrl` back onto a feed host, which is not a
   * choice an integrator who fronts the API deliberately can make.
   *
   * INJECTED CONFIG ONLY: unlike every other field here, this is not read
   * from the query string. See {@link readConfig}.
   */
  analytics?: boolean | { endpoint?: string };
}

declare global {
  interface Window {
    HAVIK_EMBED_CONFIG?: Partial<EmbedConfig>;
  }
}

const URN_RE = /^[A-Za-z0-9:_.-]{1,128}$/;

/**
 * Normalize an injected `analytics` value.
 *
 * The host page writes this object, so it is trusted — but it is plain JS
 * with no type checking at the boundary, and a wrong shape is worse than a
 * missing one here: `analytics: 'yes'` reaches createPlayer as a truthy
 * non-object, reads as `{ endpoint: undefined }`, and silently falls back to
 * derivation. Anything unrecognized becomes undefined instead, so the
 * documented default applies and the SDK's own diagnostics see an absent
 * option rather than a malformed one.
 */
function readAnalytics(v: unknown): EmbedConfig['analytics'] {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'object' && v !== null) {
    const endpoint = (v as { endpoint?: unknown }).endpoint;
    return typeof endpoint === 'string' ? { endpoint } : {};
  }
  return undefined;
}

/**
 * Config is read in ONE of two mutually-exclusive modes:
 *  - Production: when window.HAVIK_EMBED_CONFIG is injected server-side, ONLY it is
 *    trusted. The query string is ignored entirely, so a tampered `?baseUrl=` can
 *    never redirect the injected api-key to an attacker origin.
 *  - Dev/demo: when no injected config is present, the query string is used.
 *
 * `analytics` is deliberately absent from the query-string branch. It names a
 * telemetry sink, and a link is the wrong place to set one: `?analytics.
 * endpoint=` would let any URL that opens this page redirect a session's QoE
 * beacons somewhere else while `baseUrl` still looks legitimate. There is no
 * demo case that needs it — a dev embed points `baseUrl` at a feed host and
 * gets the derived endpoint for free. Injected config only.
 */
function readConfig(): EmbedConfig {
  const injected = window.HAVIK_EMBED_CONFIG;
  if (injected) {
    return {
      baseUrl: injected.baseUrl ?? '',
      matchUrn: injected.matchUrn ?? '',
      apiKey: injected.apiKey ?? '',
      parentOrigin: injected.parentOrigin,
      autoplay: injected.autoplay ?? true,
      muted: injected.muted ?? true,
      waitForLive: injected.waitForLive ?? true,
      analytics: readAnalytics(injected.analytics),
    };
  }
  const q = new URLSearchParams(location.search);
  return {
    baseUrl: q.get('baseUrl') ?? '',
    matchUrn: q.get('matchUrn') ?? '',
    apiKey: q.get('apiKey') ?? '',
    parentOrigin: q.get('parentOrigin') ?? undefined,
    autoplay: true,
    muted: q.get('muted') !== 'false',
    waitForLive: true,
  };
}

/**
 * Boots the iframe-side player and speaks the postMessage protocol with the host.
 * The api-key is NEVER accepted over postMessage. The bridge fails CLOSED: with no
 * trusted parentOrigin it neither accepts commands nor broadcasts events.
 */
export async function bootEmbed(): Promise<void> {
  const cfg = readConfig();
  applyFullBleedStyles();

  const video = document.createElement('video');
  video.setAttribute('playsinline', '');
  // createPlayer mounts the branded custom control bar (controls: 'custom' default).
  video.muted = cfg.muted ?? true;
  video.style.cssText =
    'width:100%;height:100%;object-fit:contain;background:#000;aspect-ratio:auto';
  document.body.appendChild(video);

  const trustedOrigin = cfg.parentOrigin && cfg.parentOrigin !== 'null' ? cfg.parentOrigin : null;
  if (!trustedOrigin) {
    console.warn(
      'havik-player: embed has no parentOrigin — the postMessage bridge is disabled ' +
        '(no events posted, no commands accepted). mountEmbed sets parentOrigin automatically.',
    );
  }

  const post = (msg: FrameToHost) => {
    if (!trustedOrigin) return; // never broadcast to '*'
    try {
      window.parent?.postMessage(msg, trustedOrigin);
    } catch {
      // parent may be gone
    }
  };

  if (!cfg.baseUrl || !cfg.matchUrn || !cfg.apiKey) {
    post({
      type: 'oddin:error',
      code: 'INTERNAL',
      message: 'embed missing baseUrl/matchUrn/apiKey',
    });
    return;
  }

  let player: Player | null = null;

  const wire = (p: Player) => {
    p.on('statechange', (state) => post({ type: 'oddin:state', state }));
    p.on('ready', () =>
      post({
        type: 'oddin:tracks',
        quality: p.getQualityLevels(),
        audio: p.getAudioTracks(),
        text: p.getTextTracks(),
      }),
    );
    p.on('waiting', (w) =>
      post({ type: 'oddin:waiting', retryInMs: w.retryInMs, attempt: w.attempt, phase: w.phase }),
    );
    p.on('qualitychange', (index) => post({ type: 'oddin:qualitychange', index }));
    p.on('audiotrackchange', (id) => post({ type: 'oddin:audiotrackchange', id }));
    p.on('texttrackchange', (id) => post({ type: 'oddin:texttrackchange', id }));
    p.on('stats', (s) =>
      post({
        type: 'oddin:stats',
        droppedFrames: s.droppedFrames,
        latencySeconds: s.latencySeconds,
        bandwidthKbps: s.bandwidthKbps,
        levelHeight: s.levelHeight,
        lowLatency: s.lowLatency,
        isLive: s.isLive,
        atLiveEdge: s.atLiveEdge,
      }),
    );
    p.on('error', (e) => post({ type: 'oddin:error', code: e.code, message: e.message }));
    p.on('autoplayblocked', () => {
      post({ type: 'oddin:autoplayblocked' });
      showTapToPlay(video, p);
    });
  };

  // Switch matches in place (destroy + re-create) rather than re-navigating — this
  // preserves injected config and keeps the api-key out of the URL/history.
  const startPlayer = async (matchUrn: string): Promise<void> => {
    player?.destroy();
    player = null;
    try {
      const p = await createPlayer({
        video,
        baseUrl: cfg.baseUrl,
        matchUrn,
        credential: { apiKey: cfg.apiKey },
        autoplay: cfg.autoplay,
        muted: cfg.muted,
        waitForLive: cfg.waitForLive,
        analytics: cfg.analytics,
      });
      player = p;
      // The custom bar wraps <video> in a `.havik-player` container; make it
      // fill the iframe (the default skin is content-sized 16:9 otherwise).
      const container = video.parentElement;
      if (container?.classList.contains('havik-player')) {
        container.style.cssText =
          'position:absolute;inset:0;width:100%;height:100%;aspect-ratio:auto';
      }
      wire(p);
    } catch (e) {
      const code = e instanceof PlaybackError ? e.code : 'INTERNAL';
      const message = e instanceof Error ? e.message : String(e);
      post({ type: 'oddin:error', code, message });
    }
  };

  const dispose = () => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('pagehide', dispose);
    player?.destroy();
    player = null;
  };

  const handleCommand = (msg: HostToFrame): void => {
    switch (msg.type) {
      case 'oddin:play':
        void player?.play();
        break;
      case 'oddin:pause':
        player?.pause();
        break;
      case 'oddin:setMuted':
        if (typeof msg.muted === 'boolean') player?.setMuted(msg.muted);
        break;
      case 'oddin:setVolume':
        if (typeof msg.volume === 'number' && Number.isFinite(msg.volume)) {
          player?.setVolume(msg.volume);
        }
        break;
      case 'oddin:setQuality':
        if (typeof msg.index === 'number') player?.setQuality(msg.index);
        break;
      case 'oddin:setMaxBitrate':
        if (
          msg.bitrate === null ||
          (typeof msg.bitrate === 'number' && Number.isFinite(msg.bitrate))
        ) {
          player?.setMaxBitrate(msg.bitrate);
        }
        break;
      case 'oddin:setAudioTrack':
        if (typeof msg.id === 'number') player?.setAudioTrack(msg.id);
        break;
      case 'oddin:setTextTrack':
        if (typeof msg.id === 'number') player?.setTextTrack(msg.id);
        break;
      case 'oddin:seekToLive':
        player?.seekToLive();
        break;
      case 'oddin:enterPip':
        void player?.enterPictureInPicture();
        break;
      case 'oddin:exitPip':
        void player?.exitPictureInPicture();
        break;
      case 'oddin:retry':
        void player?.retry();
        break;
      case 'oddin:load':
        if (typeof msg.matchUrn === 'string' && URN_RE.test(msg.matchUrn)) {
          void startPlayer(msg.matchUrn);
        }
        break;
      case 'oddin:destroy':
        dispose();
        break;
    }
  };

  const onMessage = (ev: MessageEvent) => {
    if (!trustedOrigin || ev.origin !== trustedOrigin) return;
    if (!isOddinMessage(ev.data)) return;
    handleCommand(ev.data as HostToFrame);
  };

  if (trustedOrigin) window.addEventListener('message', onMessage);
  window.addEventListener('pagehide', dispose, { once: true });

  await startPlayer(cfg.matchUrn);
  post({ type: 'oddin:ready', protocol: PROTOCOL_VERSION });
}

function showTapToPlay(video: HTMLVideoElement, player: Player): void {
  if (document.getElementById('havik-tap')) return;
  const overlay = document.createElement('button');
  overlay.id = 'havik-tap';
  overlay.textContent = '▶  Tap to play';
  overlay.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;border:0;background:rgba(0,0,0,.55);' +
    'color:#fff;font:600 18px system-ui,sans-serif;cursor:pointer;z-index:9';
  overlay.addEventListener('click', () => {
    overlay.remove();
    void player.play(); // a user gesture — play() now succeeds
  });
  document.body.appendChild(overlay);
  void video; // overlay covers the element; kept for signature clarity
}

function applyFullBleedStyles(): void {
  const style = document.createElement('style');
  style.textContent =
    'html,body{margin:0;height:100%;background:#000;overflow:hidden}body{display:flex}';
  document.head.appendChild(style);
}
